class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector(
      "[data-predictive-search]"
    );
    this.allPredictiveSearchInstances =
      document.querySelectorAll("predictive-search");
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = "";

    // The floating card, its scrolling box, the dim layer around it, and what the card shows
    // while the box is empty (snippets/jd-search-idle.liquid). Decision 226.
    this.panel = this.querySelector("[data-jd-search-panel]");
    this.scroller = this.querySelector("[data-jd-search-scroll]");
    this.backdrop = this.querySelector("[data-jd-search-backdrop]");
    this.idle = this.querySelector("[data-jd-search-idle]");
    this.recent = this.querySelector("[data-jd-search-recent]");
    this.onViewportChange = this.onViewportChange.bind(this);

    this.setupEventListeners();
  }

  disconnectedCallback() {
    // The theme editor re-renders the header: the old element must not keep its window
    // listeners.
    this.unfollowViewport();
  }

  setupEventListeners() {
    this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));

    if (this.panel) {
      // Keep the focus in the box while the visitor taps inside the panel. Without this, the
      // box's focusout closes the panel before the tap's click arrives.
      this.panel.addEventListener("mousedown", (event) => event.preventDefault());
      this.panel.addEventListener("click", this.onPanelClick.bind(this));
    }

    if (this.backdrop) {
      // A tap on the dim layer closes the search, as a tap outside the other pop-ups does
      // (decision 226). The mousedown keeps the focus, so only the click closes.
      this.backdrop.addEventListener("mousedown", (event) => event.preventDefault());
      this.backdrop.addEventListener("click", this.onBackdropClick.bind(this));
    }

    this.input.addEventListener("focus", this.onFocus.bind(this));
    // After Escape the box keeps the focus with the pop-up closed, so a tap on the box
    // fires no focus event. The click opens it again.
    this.input.addEventListener("click", () => {
      if (!this.isOpen) this.onFocus();
    });
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim().replace(" ", " OR ");
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      this.openIdle();
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    // An empty box never submits: the owner chose this on 2026-09-24, so the search page is
    // never opened with an empty term from here.
    if (!this.getQuery().length) {
      event.preventDefault();
      return;
    }

    // A highlighted option goes by its own click, once: a link navigates, and the
    // "search for" button submits as the submitter. Any other submit while an option is
    // highlighted would be a second one. The flag covers a browser without
    // event.submitter (decision 226).
    const selected = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]'
    );
    if (
      selected &&
      !this.clickingOption &&
      !(event.submitter && event.submitter === selected)
    ) {
      event.preventDefault();
      // search-form.js has already joined the words with " OR " for the search page.
      // This submit does not go, so the box shows what the shopper typed.
      this.input.value = this.input.value.replace(/ OR /g, " ");
      return;
    }

    // search-form.js has already turned the spaces into " OR ". Keep the words as typed.
    this.saveRecentSearch(this.input.value.replace(/ OR /g, " "));
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = "";
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
      this.openIdle();
    }
  }

  onFocus() {
    if (this.refocusing) return;
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) {
      this.openIdle();
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute("results") === "true") {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    // Escape closed the pop-up on keydown. The empty-box rule below would open it again.
    if (event.key === "Escape") return;

    if (!this.getQuery().length) {
      this.close(true);
      if (this.contains(document.activeElement)) this.openIdle();
    }
    event.preventDefault();

    switch (event.key) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.key === "ArrowUp" || event.key === "ArrowDown") {
      event.preventDefault();
      return;
    }

    // An input method uses Escape and Enter for its own text, and a held key repeats.
    const composing = event.isComposing || event.keyCode === 229;

    if (event.key === "Escape") {
      if (composing || !event.isTrusted) return;
      // Escape only closes. Without preventDefault the search box would also clear itself,
      // also on each repeat of a held key, which then does nothing more.
      event.preventDefault();
      if (event.repeat) return;
      // close() removes `open`, and frontend/utils/predictive-search-back-to-close.js then
      // takes its history entry back, as for a tap outside. The focus stays in the box, or
      // goes back to it from a button inside the card, which is about to hide (decision 226).
      const fromCard = Boolean(this.panel?.contains(event.target));
      if (this.isOpen || this.hasAttribute("open")) this.close();
      if (fromCard) {
        // This focus must not run onFocus(), which would open the pop-up again.
        this.refocusing = true;
        try {
          this.input.focus({ preventScroll: true });
        } finally {
          this.refocusing = false;
        }
      }
      return;
    }

    // Enter on a button inside the card, such as the scroll hint's arrow, is that button's.
    if (event.key === "Enter" && event.target === this.input) {
      if (composing) return;
      // On keydown, so a highlighted option is followed once, before the browser can also
      // submit the form (decisions 223 and 226). Without a highlighted option, Enter
      // submits the search as usual.
      const selected = this.getSelectedOption();
      if (!selected && !event.repeat) return;
      event.preventDefault();
      if (selected && !event.repeat) this.selectOption();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      "[data-predictive-search-search-for-text]"
    );
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, "g")).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) return;

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(
      this.querySelectorAll("li, button.predictive-search__item")
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }

  getSelectedOption() {
    return this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]'
    );
  }

  selectOption() {
    const selectedOption = this.getSelectedOption();
    if (!selectedOption) return;

    // The "search for" button submits the form inside click(). The flag tells onFormSubmit
    // that this submit is the option's own.
    this.clickingOption = true;
    try {
      selectedOption.click();
    } finally {
      this.clickingOption = false;
    }
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(
      `/en${routes.predictive_search_url}?q=${encodeURIComponent(
        searchTerm
      )}&section_id=predictive-search`,
      {
        signal: this.abortController.signal,
      }
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text.replace("/en/", "/"), "text/html")
          .querySelector("#shopify-section-predictive-search").innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach(
          (predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          }
        );
        // A panel closed while the request ran (Back, or focus left) stays
        // closed: opening it again would lock the page and push a history
        // entry nobody asked for (decision 225).
        if (this.isOpen) this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText =
      this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.removeAttribute("idle");
    this.followViewport();
    // `loading` alone already shows the panel. `open` too, so
    // frontend/utils/predictive-search-back-to-close.js sees it, locks the page and
    // closes it on Back (decision 225). close() removes both.
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);
    this.isOpen = true;
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    // New results start at their top, not where the old ones were scrolled to.
    if (this.scroller) this.scroller.scrollTop = 0;
    this.setAttribute("results", true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    this.setLiveRegionText(
      this.querySelector("[data-predictive-search-live-region-count-value]")
        .textContent
    );
  }

  // The card floats 8px under the top bar, which is not always 72px high, and is at most as
  // tall as the space above the keyboard, less a 16px gap (decision 226). The two values go
  // on this element, so the card and the dim layer both read them. With the keyboard closed,
  // the phone's home-bar area is left free too.
  placePanel() {
    if (!this.panel) return;
    const bar = document.querySelector(".section-header");
    const barBottom = bar ? Math.max(0, bar.getBoundingClientRect().bottom) : 72;
    const view = window.visualViewport;
    const viewBottom = view ? view.offsetTop + view.height : window.innerHeight;
    const keyboardOpen = view
      ? window.innerHeight - view.height * view.scale > 120
      : false;
    const safeBottom = keyboardOpen ? 0 : this.readSafeAreaBottom();
    const max = Math.max(96, viewBottom - (barBottom + 8) - 16 - safeBottom);
    // A value equal to the last one written is skipped, so a scroll or resize that does not
    // move the card leaves the card and the dim layer alone (decision 243).
    const barValue = `${barBottom}px`;
    const maxValue = `${Math.floor(max)}px`;
    if (this.placedBarBottom !== barValue) {
      this.placedBarBottom = barValue;
      this.style.setProperty("--jd-search-bar-bottom", barValue);
    }
    if (this.placedPanelMax !== maxValue) {
      this.placedPanelMax = maxValue;
      this.style.setProperty("--jd-search-panel-max", maxValue);
    }
  }

  // env(safe-area-inset-bottom) has no JavaScript reading, so a hidden probe measures it.
  readSafeAreaBottom() {
    if (!this.safeAreaProbe) {
      this.safeAreaProbe = document.createElement("span");
      this.safeAreaProbe.setAttribute("aria-hidden", "true");
      this.safeAreaProbe.style.cssText =
        "position:fixed;visibility:hidden;pointer-events:none;width:0;height:0;padding-bottom:env(safe-area-inset-bottom,0px)";
      this.appendChild(this.safeAreaProbe);
    }
    return parseFloat(getComputedStyle(this.safeAreaProbe).paddingBottom) || 0;
  }

  // While the pop-up is open the keyboard opens and closes, the screen turns, and iOS moves
  // the visible area. Each change places the card again, once per frame. Added once per
  // opening, removed in closeResults().
  // The window scroll stays (decision 243): the page lock is only `overflow: hidden` on the
  // body, set a moment later by a MutationObserver in back-to-close.js, which also scrolls
  // the page 1px itself; and iOS can scroll the page to show the focused field. Any of these
  // can move the top bar, and nothing here proves a visualViewport event comes with it. The
  // skip in placePanel keeps the extra calls cheap.
  followViewport() {
    if (!this.panel) return;
    this.placePanel();
    if (this.followingViewport) return;
    this.followingViewport = true;
    const view = window.visualViewport;
    if (view) {
      view.addEventListener("resize", this.onViewportChange);
      view.addEventListener("scroll", this.onViewportChange);
    }
    window.addEventListener("resize", this.onViewportChange);
    window.addEventListener("scroll", this.onViewportChange, { passive: true });
  }

  unfollowViewport() {
    if (this.placeFrame) cancelAnimationFrame(this.placeFrame);
    this.placeFrame = 0;
    if (!this.followingViewport) return;
    this.followingViewport = false;
    const view = window.visualViewport;
    if (view) {
      view.removeEventListener("resize", this.onViewportChange);
      view.removeEventListener("scroll", this.onViewportChange);
    }
    window.removeEventListener("resize", this.onViewportChange);
    window.removeEventListener("scroll", this.onViewportChange, { passive: true });
  }

  onViewportChange() {
    if (this.placeFrame) return;
    this.placeFrame = requestAnimationFrame(() => {
      this.placeFrame = 0;
      this.placePanel();
    });
  }

  openIdle() {
    if (!this.idle || document.activeElement !== this.input) return;
    this.renderRecentSearches();
    // The idle box starts at its top. A key in an empty box closes and opens it again, so
    // that also scrolls it back to the top.
    if (this.scroller) this.scroller.scrollTop = 0;
    this.followViewport();
    // `open` too, so frontend/utils/predictive-search-back-to-close.js closes it on Back and
    // the sticky top bar stays put. The CSS shows the idle part and hides the results part.
    this.setAttribute("idle", true);
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);
    this.isOpen = true;
  }

  onPanelClick(event) {
    const query = event.target.closest("[data-jd-search-query]");
    if (query) {
      this.saveRecentSearch(query.dataset.jdSearchQuery);
      return;
    }

    const recentLink = event.target.closest("[data-jd-search-recent-link]");
    if (recentLink) {
      this.saveRecentSearch(recentLink.dataset.term);
      return;
    }

    const remove = event.target.closest("[data-jd-search-recent-remove]");
    if (remove) {
      this.writeRecentSearches(
        this.readRecentSearches().filter((term) => term !== remove.dataset.term)
      );
      this.renderRecentSearches();
      return;
    }

    if (event.target.closest("[data-jd-search-recent-clear]")) {
      this.writeRecentSearches([]);
      this.renderRecentSearches();
    }
  }

  // Recent searches live only in this browser. Storage can be blocked or full, so every
  // read and write is guarded, and the pop-up works without them.
  readRecentSearches() {
    try {
      const terms = JSON.parse(localStorage.getItem("jd-recent-searches"));
      return Array.isArray(terms)
        ? terms.filter((term) => typeof term === "string")
        : [];
    } catch (error) {
      return [];
    }
  }

  writeRecentSearches(terms) {
    try {
      localStorage.setItem("jd-recent-searches", JSON.stringify(terms));
    } catch (error) {
      // Storage is blocked or full. The list simply stays as it was.
    }
  }

  saveRecentSearch(rawTerm) {
    const term = (rawTerm || "").trim().replace(/\s+/g, " ");
    if (!term) return;
    const others = this.readRecentSearches().filter(
      (saved) => saved.toLowerCase() !== term.toLowerCase()
    );
    this.writeRecentSearches([term, ...others].slice(0, 5));
  }

  renderRecentSearches() {
    if (!this.recent) return;
    const list = this.recent.querySelector("[data-jd-search-recent-list]");
    const rowTemplate = this.querySelector("template[data-jd-search-recent-row]");
    if (!list || !rowTemplate) return;

    const terms = this.readRecentSearches();
    const searchUrl = this.recent.dataset.searchUrl;
    const removeLabel = this.recent.dataset.removeLabel || "";

    list.replaceChildren(
      ...terms.map((term) => {
        const row = rowTemplate.content.firstElementChild.cloneNode(true);
        const link = row.querySelector("[data-jd-search-recent-link]");
        const remove = row.querySelector("[data-jd-search-recent-remove]");
        link.href = `${searchUrl}?q=${encodeURIComponent(
          term.split(" ").join(" OR ")
        )}&options%5Bprefix%5D=last`;
        link.dataset.term = term;
        row.querySelector("[data-jd-search-recent-text]").textContent = term;
        remove.dataset.term = term;
        remove.setAttribute("aria-label", removeLabel.replace("[terms]", term));
        return row;
      })
    );
    this.recent.hidden = terms.length === 0;
  }

  open() {
    // No height on the results themselves: the card limits its own height, and the scroll
    // hint watches the results grow (decision 226).
    this.followViewport();
    this.removeAttribute("idle");
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("loading");
    this.removeAttribute("idle");
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);
    this.unfollowViewport();
  }

  // The dim layer's tap (decision 226). close() removes `open`, so
  // frontend/utils/predictive-search-back-to-close.js takes the history entry back once.
  // The blur closes the keyboard; its focusout then finds the pop-up closed already.
  onBackdropClick() {
    this.close();
    this.input.blur();
  }
}

customElements.define("predictive-search", PredictiveSearch);
